// SUN-281 (7-9): picking a contact / account / warranty fills the fields that record already holds.
// A new pick REPLACES what an earlier pick filled; text the user typed (or a saved value on Edit) is never wiped.

const clean = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/**
 * Put `fill` into `current`. A key the fill has a value for is replaced; a key the fill lacks is cleared
 * only if an earlier pick put it there (`auto` remembers which). Returns the new values; updates `auto`.
 */
export function applyFill<T extends object, K extends keyof T>(
  current: T,
  fill: Partial<Record<K, string | undefined>>,
  keys: readonly K[],
  auto: Set<string>,
): T {
  const next = { ...current } as Record<string, unknown>;
  for (const key of keys) {
    const v = fill[key];
    if (v) { next[String(key)] = v; auto.add(String(key)); }
    else if (auto.has(String(key))) { next[String(key)] = ''; auto.delete(String(key)); }
  }
  return next as T;
}

/** A contact's address as the meeting form needs it. Address JSON is { street, city, state, pincode }; city also lives on the contact. */
export function contactMeetingAddress(contact: { address?: unknown; city?: string | null } | null | undefined) {
  const a = (contact?.address && typeof contact.address === 'object' ? contact.address : {}) as Record<string, unknown>;
  return {
    addressLine1: clean(a.street),
    city: clean(a.city) ?? clean(contact?.city),
    pincode: clean(a.pincode),
  };
}

/** The account a contact belongs to ('' when none). */
export function accountIdOfContact(contact: { accountId?: string | null; account?: { id?: string } | null } | null | undefined): string {
  return clean(contact?.accountId) ?? clean(contact?.account?.id) ?? '';
}

/** The account a warranty belongs to ('' when none). */
export function accountIdOfWarranty(w: { accountId?: string | null; account?: { id?: string } | null } | null | undefined): string {
  return clean(w?.accountId) ?? clean(w?.account?.id) ?? '';
}

/** What an account holds for a warranty contact. */
export function warrantyContactFromAccount(account: { phone?: string | null; email?: string | null } | null | undefined) {
  return { contactPhone: clean(account?.phone), contactEmail: clean(account?.email) };
}
